class BandCupSeparator extends HTMLElement {
  constructor() {
    super();

    this.variantPicker = document.querySelector("variant-picker");

    if (!this.variantPicker) {
      return;
    }

    this.defaultSizeDropdown = this.variantPicker?.querySelector("select[name='options[Size]']");
    this.bandDropdown = this.querySelector("#Option-band");
    this.cupDropdown = this.querySelector("#Option-cup");
    this.combinationScript = this.querySelector('#band-cup-combinations');
    this.soldOutText = this.dataset.soldOutText || "Unavailable";

    this.combinations = this.getBandCupCombinations();

    // Store original option texts for both dropdowns
    this.originalBandOptionTexts = new Map();
    if (this.bandDropdown) {
      Array.from(this.bandDropdown.options).forEach(option => {
        this.originalBandOptionTexts.set(option.value, option.textContent);
      });
    }

    this.originalCupOptionTexts = new Map();
    if (this.cupDropdown) {
      Array.from(this.cupDropdown.options).forEach(option => {
        this.originalCupOptionTexts.set(option.value, option.textContent);
      });
    }

    // --- New properties for color change detection ---
    this._lastSelectedColorValue = null; // Stores the color value of the last processed variant
    this._colorOptionKey = 'option1'; // Default assumption: Color is the first option (option1)
                                      // This will be refined in init() if data-option-index is available.
    // --- End new properties ---

    this.init();
  }

  init() {
    if (this.bandDropdown) {
      this.bandDropdown.addEventListener("change", () => this.handleDropdownChange('band'));
    } else {
      console.error("Band dropdown (#Option-band) not found.");
    } 

    if (this.cupDropdown) {
      this.cupDropdown.addEventListener("change", () => this.handleDropdownChange('cup'));
    } else {
      console.error("Cup dropdown not found.");
    } 

    if (!this.variantPicker) {
      console.error("Variant picker (<variant-picker>) not found.");
      return; // Cannot proceed without variantPicker
    }
    if (!this.defaultSizeDropdown) {
      console.error("Default size dropdown not found inside variant picker.");
    }

    // --- Determine the actual option key for 'Color' ---
    // Shopify often adds data-option-index to the select elements for product options.
    // We'll use this to map 'Color' to 'option1', 'option2', or 'option3' on the variant object.
    const colorSelectElement = document.querySelector('select[name="options[Color]"]');
    if (colorSelectElement) {
        const optionIndex = colorSelectElement.dataset.optionIndex;
        if (optionIndex) {
            this._colorOptionKey = `option${optionIndex}`;
        } else {
            console.warn("Could not determine color option index from data-option-index on select[name='options[Color]'].");
        }
    } else {
        console.warn("Color select element (select[name='options[Color]']) not found. Color change detection might not work as expected.");
    } 
    // --- End color option key determination ---


    // Listen for the variant:update event on the variantPicker itself
    this.variantPicker.addEventListener('variant:update', this.handleVariantUpdate.bind(this));

    // Initial update of dropdowns when the component loads
    this.updateAvailableOptions('band');
    this.updateAvailableOptions('cup');

    this.bandCupSizeChange();
  }

  getBandCupCombinations() {
    if (!this.combinationScript) {
        console.error('Band-cup combinations data not found');
        return [];
    }

    try {
        return JSON.parse(this.combinationScript.textContent);
    } catch (error) {
        console.error('Error parsing band-cup combinations:', error);
        return [];
    }
  }

  /**
   * Handles a change event from either the band or cup dropdown.
   * Updates the options in the *other* dropdown and then triggers the Shopify variant change.
   * @param {string} changedDropdownType - 'band' or 'cup', indicating which dropdown changed.
   */
  handleDropdownChange(changedDropdownType) {
    if (changedDropdownType === 'band') {
      this.updateAvailableOptions('cup'); // Update cup options based on the new band selection
    } else if (changedDropdownType === 'cup') {
      this.updateAvailableOptions('band'); // Update band options based on the new cup selection
    }

   // trigger the variant change in the default variant picker
    this.bandCupSizeChange();
  }

  /**
   * Handles the variant:update event dispatched by the main variant-picker.
   * This indicates a new variant (e.g., color) has been selected.
   * We need to re-fetch the band-cup combinations for the new variant.
   * @param {CustomEvent} event - The variant:update event. The event.detail is the variant object.
   */
  async handleVariantUpdate(event) {

    // --- Color change detection logic ---
    const currentVariantColorValue = event.detail.resource[this._colorOptionKey];

    // If this is the first variant update, or the color has actually changed
    if (this._lastSelectedColorValue === null || currentVariantColorValue !== this._lastSelectedColorValue) {
      this._lastSelectedColorValue = currentVariantColorValue; // Update the last known color
    } else {
      return;
    }


    // The event.detail contains information about the newly selected variant.
    // We need the product URL and the new variant ID to fetch the correct section.
    const newColorOptionId = event.detail.sourceId; // The variant ID is directly on event.detail

    // Construct the URL to fetch the band-cup-combinations section for the new variant.
    // Use the product URL from the variantPicker or window.location.pathname as a fallback.
    const currentProductUrl = this.variantPicker.dataset.productUrl || window.location.pathname.split('?')[0];
    const url = new URL(currentProductUrl, window.location.origin); // Use window.location.origin for base URL
    url.searchParams.set('option_values', newColorOptionId);
    url.searchParams.set('section_id', 'band-cup-combinations'); // Request our specific section

    const urlString = url.toString();
    try {
      const response = await fetch(urlString);
      if (!response.ok) {
        throw new Error(`Failed to fetch section: ${response.statusText}`);
      }

      const text = await response.text();
      const htmlMarkup = new DOMParser().parseFromString(text, 'text/html');

      const newCombinationScript = htmlMarkup.querySelector('#band-cup-combinations');
      if (!newCombinationScript) {
        console.error('No new #band-cup-combinations script found in the section rendering response.');
        return;
      }

      // Update the content of our existing combination script tag
      this.combinationScript.textContent = newCombinationScript.textContent;

      // Re-parse the combinations data
      this.combinations = this.getBandCupCombinations();
      // Re-evaluate and update the band/cup dropdowns based on the new combinations
      this.updateAvailableOptions('band');
      this.updateAvailableOptions('cup');

      this.bandCupSizeChange();

    } catch (error) {
      console.error('Error fetching or updating band-cup combinations:', error);
    }
  }


  /**
   * Updates the enabled/disabled state and text of options in a specified dropdown
   * based on the current selection of the *other* dropdown and available combinations.
   * @param {string} dropdownToUpdate - 'band' or 'cup', indicating which dropdown's options to modify.
   */
  updateAvailableOptions(dropdownToUpdate) {
    const currentBand = this.bandDropdown.value;
    const currentCup = this.cupDropdown.value;

    let targetDropdown;
    let originalOptionTextsMap; // Map to store original text for this dropdown
    let filterKey;
    let optionKey;
    let currentFilterValue;

    if (dropdownToUpdate === 'band') {
      targetDropdown = this.bandDropdown;
      originalOptionTextsMap = this.originalBandOptionTexts;
      filterKey = 'cup';
      optionKey = 'band';
      currentFilterValue = currentCup;
    } else if (dropdownToUpdate === 'cup') {
      targetDropdown = this.cupDropdown;
      originalOptionTextsMap = this.originalCupOptionTexts;
      filterKey = 'band';
      optionKey = 'cup';
      currentFilterValue = currentBand;
    } else {
      console.error('Invalid dropdown type to update:', dropdownToUpdate);
      return;
    }

    const options = Array.from(targetDropdown.options);
    let currentSelectionStillPossible = false;

    options.forEach(option => {
      // Restore original text content first before re-evaluating availability
      option.textContent = originalOptionTextsMap.get(option.value) || option.textContent;

      if (option.value === "") { // Keep the default "Select..." option enabled and unchanged
        option.disabled = false;
        return;
      }

      // Find all combinations for this specific option value and the current filter value
      const relevantCombinations = this.combinations.filter(combo =>
          combo[optionKey] === option.value && combo[filterKey] === currentFilterValue
      );

      // Check if there's at least one combination (available or not) for this option value
      const hasAnyCombination = relevantCombinations.length > 0;

      // Determine if AT LEAST ONE relevant combination is out of stock (for "Unavailable" text)
      const isPartiallyOrCompletelyUnavailable = hasAnyCombination && relevantCombinations.some(combo => !combo.available);


      // Logic for disabling: An option should be disabled ONLY if it's not a valid combination at all.
      option.disabled = !hasAnyCombination;

      // Append "Sold Out" if it's a valid combination AND at least one of its underlying variants is out of stock.
      if (isPartiallyOrCompletelyUnavailable) {
          option.textContent += " - " + this.soldOutText;
      }

      // Check if the currently selected option is still possible (i.e., not disabled by the new logic)
      if (option.selected && !option.disabled) {
          currentSelectionStillPossible = true;
      }
    });

    // If the currently selected option became disabled, or no option was selected,
    // try to select the first available option.
    if (!currentSelectionStillPossible && targetDropdown.value !== "") {
        const firstEnabledOption = options.find(opt => !opt.disabled && opt.value !== "");
        if (firstEnabledOption) {
            targetDropdown.value = firstEnabledOption.value;
        } else {
            // If no options are available for the current filter, clear the selection
            targetDropdown.value = "";
        }
    }
  }

  bandCupSizeChange() {
    if (!this.bandDropdown || !this.cupDropdown || !this.variantPicker || !this.defaultSizeDropdown) {
      return;
    }

    const bandValue = this.bandDropdown.value;
    const cupValue = this.cupDropdown.value;

    // Only proceed if both band and cup are selected
    if (!bandValue || !cupValue) {
        return;
    }

    const bandCupCombined = bandValue + cupValue;

    // Find the combination in our loaded data to check if it's a valid pair
    const selectedCombinationData = this.combinations.find(combo =>
        combo.band === bandValue && combo.cup === cupValue
    );

    if (!selectedCombinationData) {
        return;
    }

    const sizeOption = this.defaultSizeDropdown.querySelector(`option[value="${bandCupCombined}"]`);

    if (sizeOption) {
      // 1. Update the value of the internal Shopify select element
      this.defaultSizeDropdown.value = bandCupCombined;

      // 2. Create a synthetic event object.
      const syntheticEvent = {
        target: this.defaultSizeDropdown,
      };

      // 3. Directly call the variantChanged method on the variantPicker instance.
      this.variantPicker.variantChanged(syntheticEvent);
    } 
  }
}

if (!window.customElements.get("band-cup-picker")) {
  window.customElements.define("band-cup-picker", BandCupSeparator);
}
